import { Link } from "@tanstack/react-router";
import { SEO } from "@/components/SEO";

export function CookiesPage() {
  return (
    <div>
      <SEO
        title="Cookie Policy"
        description="Learn about the cookies and similar technologies used by GetSent."
        canonical="/cookies"
      />

      {/* Header */}
      <section className="relative overflow-hidden border-b bg-gradient-to-b from-slate-50/50 to-transparent py-16 dark:from-slate-950/20 sm:py-20">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Cookie Policy</h1>
          <p className="mt-4 text-muted-foreground">Last updated: January 5, 2026</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 sm:py-16">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <div className="prose prose-slate dark:prose-invert max-w-none">
            <p className="lead text-lg text-muted-foreground">
              This Cookie Policy explains how BG3 Group, LLC doing business as GetSent ("we", "us", or "our")
              uses cookies and similar technologies when you visit our website at getsent.dev.
            </p>

            <h2 className="mt-10 text-xl font-semibold">What are cookies?</h2>
            <p className="mt-4 text-muted-foreground">
              Cookies are small text files that are stored on your device (computer, tablet, or mobile) when you
              visit a website. They are widely used to make websites work more efficiently and to provide
              information to website owners.
            </p>

            <h2 className="mt-10 text-xl font-semibold">How we use cookies</h2>
            <p className="mt-4 text-muted-foreground">
              We use a minimal number of cookies, focused only on what's necessary to provide our service.
              We do not use cookies for advertising or tracking purposes.
            </p>

            <h3 className="mt-8 text-lg font-medium">Strictly Necessary Cookies</h3>
            <p className="mt-2 text-muted-foreground">
              These cookies are essential for the website to function properly. They enable core functionality
              such as security, authentication, and session management. You cannot opt out of these cookies
              as they are required for the service to work.
            </p>

            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="py-3 pr-4 text-left font-medium">Cookie Name</th>
                    <th className="py-3 pr-4 text-left font-medium">Purpose</th>
                    <th className="py-3 pr-4 text-left font-medium">Duration</th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground">
                  <tr className="border-b">
                    <td className="py-3 pr-4 font-mono text-xs">session</td>
                    <td className="py-3 pr-4">Maintains your logged-in state and authentication</td>
                    <td className="py-3 pr-4">30 days</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h2 className="mt-10 text-xl font-semibold">Analytics</h2>
            <p className="mt-4 text-muted-foreground">
              We use <a href="https://plausible.io" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-500">Plausible Analytics</a> to
              understand how visitors use our website. Plausible is a privacy-focused analytics tool that does
              not use cookies and does not collect any personal data. It is fully compliant with GDPR, CCPA,
              and PECR without requiring cookie consent.
            </p>
            <p className="mt-2 text-muted-foreground">
              Plausible does not track individual users across websites or sessions. All data is aggregated
              and no personally identifiable information is stored.
            </p>

            <h2 className="mt-10 text-xl font-semibold">Third-party cookies</h2>
            <p className="mt-4 text-muted-foreground">
              We do not use any third-party cookies for advertising, remarketing, or cross-site tracking purposes.
            </p>
            <p className="mt-2 text-muted-foreground">
              When you make a payment, you may interact with Stripe's payment interface. Stripe may set its own
              cookies for fraud prevention and security purposes. Please refer to{" "}
              <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-500">
                Stripe's Privacy Policy
              </a>{" "}
              for more information.
            </p>

            <h2 className="mt-10 text-xl font-semibold">Managing cookies</h2>
            <p className="mt-4 text-muted-foreground">
              Most web browsers allow you to control cookies through their settings. You can typically find
              these settings in the "Options" or "Preferences" menu of your browser. However, please note
              that disabling strictly necessary cookies may prevent you from using certain features of our
              service, such as staying logged in.
            </p>
            <p className="mt-2 text-muted-foreground">
              For more information about managing cookies, visit{" "}
              <a href="https://www.aboutcookies.org" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-500">
                aboutcookies.org
              </a>.
            </p>

            <h2 className="mt-10 text-xl font-semibold">Updates to this policy</h2>
            <p className="mt-4 text-muted-foreground">
              We may update this Cookie Policy from time to time to reflect changes in our practices or for
              other operational, legal, or regulatory reasons. We will notify you of any material changes by
              updating the "Last updated" date at the top of this policy.
            </p>

            <h2 className="mt-10 text-xl font-semibold">Contact us</h2>
            <p className="mt-4 text-muted-foreground">
              If you have any questions about our use of cookies, please contact us at{" "}
              <a href="mailto:privacy@getsent.dev" className="text-indigo-600 hover:text-indigo-500">
                privacy@getsent.dev
              </a>.
            </p>
          </div>

          <div className="mt-12 flex items-center gap-4 text-sm text-muted-foreground">
            <Link to="/privacy" className="hover:text-foreground">
              Privacy Policy
            </Link>
            <span>&middot;</span>
            <Link to="/terms" className="hover:text-foreground">
              Terms of Service
            </Link>
            <span>&middot;</span>
            <Link to="/" className="hover:text-foreground">
              Home
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
